import React, { useMemo } from 'react';
import { StyleSheet } from 'react-native';
import format from 'date-fns/format';
import { DNAButton, DNAChipStatusEnum, DNAText, GridList, luxColors } from '@alucio/lux-ui';
import { DNAChip } from 'src/components/DNA';

import TrainingEventContextMenu from 'src/screens/Tenants/Tabs/Events/TrainingEventContextMenu'
import { TenantORM } from 'src/state/redux/selector/tenant'
import { TrainingEventORM } from 'src/types/orms'

const styles = StyleSheet.create({
  column: {
    paddingVertical: 12,
    paddingHorizontal: 28,
  },
  itemsWrapper: {
    flex: 1,
    flexDirection: 'column',
  },
  itemTextColor: {
    color: luxColors.contentText.tertiary,
  },
});

enum TrainingEventStatus {
  IN_PROGRESS = 'IN PROGRESS',
  UPCOMING = 'UPCOMING',
  COMPLETED = 'COMPLETED',
}

interface Props {
  trainingEvent: TrainingEventORM
  tenantORM: TenantORM,
}

const DNATrainingEventDefaultRow = (props: Props) => {
  const { trainingEvent } = props

  // considering typing the return type
  const getTrainingEventStatus = () => {
    // note we are assuming that endDateTime is always after startDateTime.
    // consider checking here if endDateTime is before startDateTime then throw error for better code quality
    const { startDateTime, endDateTime } = trainingEvent.model
    const now = Date.now()
    const startDateTimeStamp = new Date(startDateTime).getTime()
    const endDateTimeStamp = new Date(endDateTime).getTime()
    if (startDateTimeStamp > now) {
      return {
        text: TrainingEventStatus.UPCOMING,
        status: DNAChipStatusEnum.secondary,
      }
    } else if (startDateTimeStamp < now && endDateTimeStamp > now) {
      return {
        text: TrainingEventStatus.IN_PROGRESS,
        status: DNAChipStatusEnum.primary,
      }
    } else {
      return {
        text: TrainingEventStatus.COMPLETED,
        status: DNAChipStatusEnum.success,
      }
    }
  }
  const trainingEventStatus = useMemo(
    () => getTrainingEventStatus(),
    [trainingEvent.model.startDateTime, trainingEvent.model.endDateTime])

  return (
    <GridList.Row
      interactive="hoverable"
      hoverColor={luxColors.backgroundColor.secondary}
    >
      {/* Title */}
      <GridList.Col style={styles.column} span={6}>
        <DNAText numberOfLines={1} style={styles.itemTextColor}>
          {trainingEvent.model.title}
        </DNAText>
      </GridList.Col>
      {/* Start time */}
      <GridList.Col style={styles.column} span={4}>
        <DNAText style={styles.itemTextColor}>
          {format(new Date(trainingEvent.model.startDateTime), 'LLL d, y, p')} PST
        </DNAText>
      </GridList.Col>
      {/* End time */}
      <GridList.Col style={styles.column} span={4}>
        <DNAText numberOfLines={1} style={styles.itemTextColor}>
          {format(new Date(trainingEvent.model.endDateTime), 'LLL d, y, p')} PST
        </DNAText>
      </GridList.Col>
      {/* status */}
      <GridList.Col style={styles.column} span={3}>
        <DNAText numberOfLines={1} style={styles.itemTextColor}>
          <DNAChip.Beacon status={trainingEventStatus.status} children={trainingEventStatus.text} />
        </DNAText>
      </GridList.Col>
      <GridList.Col style={[styles.column, { justifyContent: 'center' }]} span={1}>
        <TrainingEventContextMenu trainingEvent={trainingEvent}>
          <DNAButton
            appearance="ghost"
            status="subtle"
            iconLeft="dots-three-vertical-bold"
          />
        </TrainingEventContextMenu>
      </GridList.Col>
    </GridList.Row>
  );
}

export default DNATrainingEventDefaultRow
